import {
  billingMethodEnum,
  customerMembers,
  customers,
  customerStatusEnum,
  customerTypeEnum,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Base schemas
export const selectCustomerSchema = createSelectSchema(customers)
export const insertCustomerSchema = createInsertSchema(customers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updateCustomerSchema = insertCustomerSchema.partial()

// Schema for customer name update by owner (limited to name field only)
export const updateCustomerNameSchema = z.object({
  name: z.string().min(1, 'Name is required'),
})

// Customer with user schema for creation
export const createCustomerWithUserSchema = z.object({
  // Customer fields
  name: z.string().min(1, 'Name is required'),
  type: z.enum(customerTypeEnum.enumValues).default('household'),
  status: z.enum(customerStatusEnum.enumValues).default('active'),
  billingMethod: z.enum(billingMethodEnum.enumValues).default('email'),
  notes: z.string().optional(),
  // Primary user fields
  user: z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    phoneNumber: z.string().optional(),
  }),
})

// Customer member schemas
export const selectCustomerMemberSchema = createSelectSchema(customerMembers)
export const insertCustomerMemberSchema = createInsertSchema(
  customerMembers,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// Customer with relations
export const selectCustomerWithRelationsSchema = selectCustomerSchema.extend({
  members: z
    .array(
      selectCustomerMemberSchema.extend({
        user: z.object({
          id: z.number(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string().nullable(),
          phoneNumber: z.string().nullable(),
        }),
      }),
    )
    .optional(),
  properties: z
    .array(
      z.object({
        id: z.number(),
        propertyType: z.string(),
        constructionYear: z.number().nullable(),
        notes: z.string().nullable(),
        address: z
          .object({
            street: z.string(),
            city: z.string(),
            postalCode: z.string().nullable(),
            latitude: z.string().nullable(),
            longitude: z.string().nullable(),
          })
          .nullable(),
        subscriptions: z
          .array(
            z.object({
              id: z.number(),
              status: z.string(),
              startDate: z.string(),
              displayName: z.string().nullable().optional(),
              basePriceMonthly: z.string().nullable().optional(),
              effectivePriceMonthly: z.string().nullable().optional(),
              additionalTerms: z.string().nullable().optional(),
              subscriptionPackageVersions: z.array(
                z.object({
                  id: z.number(),
                  quantity: z.number(),
                  sortOrder: z.number(),
                  servicePackageVersion: z.object({
                    id: z.number(),
                    versionNumber: z.number(),
                    priceMonthly: z.string(),
                  }),
                }),
              ),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
})

// Type exports
export type Customer = z.infer<typeof selectCustomerSchema>
export type CustomerWithRelations = z.infer<
  typeof selectCustomerWithRelationsSchema
>
export type CreateCustomerWithUser = z.infer<
  typeof createCustomerWithUserSchema
>
export type UpdateCustomer = z.infer<typeof updateCustomerSchema>
export type UpdateCustomerName = z.infer<typeof updateCustomerNameSchema>
export type CustomerMember = z.infer<typeof selectCustomerMemberSchema>
